import { useFetchPrintOnDemandProduct } from "@design-lab/entities/product";
import { useIsInitialState } from "@design-lab/features/design-project/model/useIsInitialState";
import { useCallback } from "react";
import { useCompositionForm } from "./useCompositionForm";

type SelectOptions = {
  catalogNumber: string;
  colorName?: string;
  mode?: "auto" | "replace" | "append";
  variants?: Record<string, number>;
};

export function useSelectProductComposition() {
  const fetchProduct = useFetchPrintOnDemandProduct();
  const { form: compositionForm, validateComposition } = useCompositionForm();
  const { isInitialState } = useIsInitialState();

  const selectProductComposition = useCallback(
    async ({
      catalogNumber,
      colorName,
      mode = "auto",
      variants = {}
    }: SelectOptions): Promise<void> => {
      // Automatically determine mode based on initial state
      let effectiveMode = mode;
      if (mode === "auto") {
        effectiveMode = isInitialState() ? "replace" : "append";
      }

      try {
        if (!catalogNumber) return;

        const product: any = await fetchProduct(catalogNumber);
        if (!product) return;

        const compositions = product?.compositions || [];
        if (!compositions.length) return;

        const targetComp =
          compositions.find((c: any) => (colorName ? c.colorName === colorName : false)) ||
          compositions.find((c: any) => c.default) ||
          compositions[0];

        if (!targetComp) return;

        // Always set current composition id
        compositionForm.setValue("currentCompositionId" as any, targetComp.id);

        if (effectiveMode === "replace") {
          compositionForm.setValue("addedCompositions" as any, [
            {
              id: targetComp.id,
              catalogId: catalogNumber,
              colorName: targetComp.colorName,
              variants
            }
          ]);
          // Register validation for the replaced composition
          compositionForm.register(`addedCompositions.0`, {
            validate: { hasVariants: validateComposition }
          });
          // Trigger form validation to ensure the UI updates
          compositionForm.trigger("addedCompositions");
        } else {
          let newIndex = 0;
          let newCompositions = [];
          const addedCompositions = compositionForm.getValues("addedCompositions") || [];

          if (addedCompositions.some((c: any) => c.id === targetComp.id)) {
            newCompositions = addedCompositions.map((c: any, index: number) => {
              if (c.id === targetComp.id) {
                newIndex = index;
                return {
                  ...c,
                  variants
                };
              }
              return c;
            });
          } else {
            newIndex = addedCompositions.length;
            newCompositions = [
              ...addedCompositions,
              {
                id: targetComp.id,
                catalogId: catalogNumber,
                colorName: targetComp.colorName,
                variants
              }
            ];
          }

          compositionForm.setValue("addedCompositions", newCompositions);
          compositionForm.register(`addedCompositions.${newIndex}`, {
            validate: { hasVariants: validateComposition }
          });
          // Trigger form validation to ensure the UI updates
          compositionForm.trigger(`addedCompositions.${newIndex}`);
        }
      } catch {
        // silently ignore initialization failures;
      }
    },
    [compositionForm, fetchProduct, validateComposition]
  );

  return { selectProductComposition };
}
